import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import NotificationCard from './components/NotificationCard';

import BulkActionsBar from './components/BulkActionsBar';
import NotificationStats from './components/NotificationStats';
import SettingsModal from './components/SettingsModal';
import { useAuth } from '../../contexts/AuthContext';
import {
  subscribeToNotifications,
  unsubscribeFromNotifications,
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  archiveNotification,
  deleteNotification
} from '../../services/notificationService';

const NotificationsCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const channelRef = useRef(null);

  // Load notifications and setup real-time subscription
  useEffect(() => {
    if (user?.id) {
      loadNotifications();
      loadUnreadCount();
      setupRealtimeSubscription();
    }

    return () => {
      if (channelRef?.current) {
        unsubscribeFromNotifications(channelRef?.current);
      }
    };
  }, [user?.id]);

  // Filter and sort notifications
  useEffect(() => {
    let filtered = [...notifications];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered?.filter(n =>
        n?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        n?.message?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered?.filter(n => n?.type === selectedType);
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered?.filter(n => n?.status === selectedStatus);
    }

    // Sort notifications
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b?.createdAt) - new Date(a?.createdAt);
        case 'date_asc':
          return new Date(a?.createdAt) - new Date(b?.createdAt);
        case 'unread_first':
          if (a?.status === 'unread' && b?.status !== 'unread') return -1;
          if (a?.status !== 'unread' && b?.status === 'unread') return 1;
          return new Date(b?.createdAt) - new Date(a?.createdAt);
        default:
          return 0;
      }
    });

    setFilteredNotifications(filtered);
  }, [notifications, searchQuery, selectedType, selectedStatus, sortBy]);

  const setupRealtimeSubscription = () => {
    if (!user?.id) return;

    channelRef.current = subscribeToNotifications(user?.id, (newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
      if (newNotification?.status === 'unread') {
        setUnreadCount((prev) => prev + 1);
      }
    });
  };

  const loadNotifications = async () => {
    if (!user?.id) return;

    setLoading(true);
    const { notifications: data } = await getNotifications(user?.id, { limit: 100 });
    setNotifications(data);
    setLoading(false);
  };

  const loadUnreadCount = async () => {
    if (!user?.id) return;
    const count = await getUnreadCount(user?.id);
    setUnreadCount(count);
  };

  const handleMarkAsRead = async (notificationId) => {
    await markAsRead(notificationId);
    setNotifications((prev) =>
      prev?.map((n) =>
        n?.id === notificationId ? { ...n, status: 'read', readAt: new Date()?.toISOString() } : n
      )
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;

    const success = await markAllAsRead(user?.id);
    if (success) {
      setNotifications((prev) =>
        prev?.map((n) => ({ ...n, status: 'read', readAt: new Date()?.toISOString() }))
      );
      setUnreadCount(0);
      setSelectedNotifications([]);
    }
  };

  const handleArchive = async (notificationId) => {
    const success = await archiveNotification(notificationId);
    if (success) {
      setNotifications((prev) => prev?.filter((n) => n?.id !== notificationId));
      setSelectedNotifications((prev) => prev?.filter((id) => id !== notificationId));
      loadUnreadCount();
    }
  };

  const handleDelete = async (notificationId) => {
    const success = await deleteNotification(notificationId);
    if (success) {
      setNotifications((prev) => prev?.filter((n) => n?.id !== notificationId));
      setSelectedNotifications((prev) => prev?.filter((id) => id !== notificationId));
      loadUnreadCount();
    }
  };

  const handleBulkMarkAsRead = async () => {
    for (const id of selectedNotifications) {
      await markAsRead(id);
    }
    setNotifications((prev) =>
      prev?.map((n) =>
        selectedNotifications?.includes(n?.id)
          ? { ...n, status: 'read', readAt: new Date()?.toISOString() }
          : n
      )
    );
    loadUnreadCount();
    setSelectedNotifications([]);
  };

  const handleBulkArchive = async () => {
    for (const id of selectedNotifications) {
      await archiveNotification(id);
    }
    setNotifications((prev) => prev?.filter((n) => !selectedNotifications?.includes(n?.id)));
    setSelectedNotifications([]);
    loadUnreadCount();
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedNotifications?.length} notification(s)?`)) return;

    for (const id of selectedNotifications) {
      await deleteNotification(id);
    }
    setNotifications((prev) => prev?.filter((n) => !selectedNotifications?.includes(n?.id)));
    setSelectedNotifications([]);
    loadUnreadCount();
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications((prev) =>
      prev?.includes(notificationId)
        ? prev?.filter((id) => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotifications?.length === filteredNotifications?.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications?.map((n) => n?.id));
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification?.status === 'unread') {
      handleMarkAsRead(notification?.id);
    }

    // Navigate based on notification type
    if (notification?.relatedType === 'booking' && notification?.relatedId) {
      navigate('/bookings');
    } else if (notification?.relatedType === 'trip' && notification?.relatedId) {
      navigate('/user-dashboard');
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedType('all');
    setSelectedStatus('all');
    setSortBy('date_desc');
  };

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'booking_confirmation', label: 'Booking Confirmations' },
    { value: 'payment_alert', label: 'Payment Alerts' },
    { value: 'trip_reminder', label: 'Trip Reminders' },
    { value: 'booking_update', label: 'Booking Updates' },
    { value: 'payment_success', label: 'Payment Success' },
    { value: 'payment_failed', label: 'Payment Failed' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'unread', label: 'Unread' },
    { value: 'read', label: 'Read' }
  ];

  const sortOptions = [
    { value: 'date_desc', label: 'Newest First' },
    { value: 'date_asc', label: 'Oldest First' },
    { value: 'unread_first', label: 'Unread First' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-28 pb-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Notifications Center</h1>
                <p className="text-muted-foreground">Manage all your travel notifications in one place</p>
              </div>
              <Button
                variant="outline"
                iconName="Settings"
                onClick={() => setIsSettingsOpen(true)}
              >
                Preferences
              </Button>
            </div>

            {/* Notification Stats */}
            <NotificationStats
              total={notifications?.length}
              unread={unreadCount}
              read={notifications?.filter((n) => n?.status === 'read')?.length}
            />
          </div>

          {/* Search and Filters */}
          <div className="bg-card border border-border rounded-xl p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="md:col-span-2">
                <Input
                  placeholder="Search notifications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full"
                />
              </div>
              <Select
                options={typeOptions}
                value={selectedType}
                onChange={setSelectedType}
                placeholder="Filter by type"
              />
              <Select
                options={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
                placeholder="Filter by status"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Select
                  options={sortOptions}
                  value={sortBy}
                  onChange={setSortBy}
                  className="w-48"
                />
                {(searchQuery || selectedType !== 'all' || selectedStatus !== 'all') && (
                  <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                    <Icon name="X" size={16} className="mr-2" />
                    Clear Filters
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  disabled={filteredNotifications?.length === 0}
                >
                  {selectedNotifications?.length === filteredNotifications?.length ? 'Deselect All' : 'Select All'}
                </Button>
                {unreadCount > 0 && (
                  <Button variant="outline" size="sm" onClick={handleMarkAllAsRead}>
                    Mark All Read
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {selectedNotifications?.length > 0 && (
            <BulkActionsBar
              selectedCount={selectedNotifications?.length}
              onMarkAsRead={handleBulkMarkAsRead}
              onArchive={handleBulkArchive}
              onDelete={handleBulkDelete}
              onCancel={() => setSelectedNotifications([])}
            />
          )}

          {/* Notifications List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading notifications...</p>
              </div>
            </div>
          ) : filteredNotifications?.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center">
              <Icon name="Bell" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No notifications found</h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery || selectedType !== 'all' || selectedStatus !== 'all' ?'Try adjusting your filters' :'You\'re all caught up!'}
              </p>
              {(searchQuery || selectedType !== 'all' || selectedStatus !== 'all') && (
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredNotifications?.map((notification) => (
                <NotificationCard
                  key={notification?.id}
                  notification={notification}
                  isSelected={selectedNotifications?.includes(notification?.id)}
                  onSelect={handleSelectNotification}
                  onClick={handleNotificationClick}
                  onMarkAsRead={handleMarkAsRead}
                  onArchive={handleArchive}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {/* Load More (if needed) */}
          {filteredNotifications?.length > 0 && filteredNotifications?.length < notifications?.length && (
            <div className="mt-6 text-center">
              <Button variant="outline" onClick={loadNotifications}>
                Load More
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Settings Modal */}
      {isSettingsOpen && (
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
        />
      )}
    </div>
  );
};

export default NotificationsCenter;